import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const AdminProfile = () => {
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Load admin profile from backend
  useEffect(() => {
    fetchAdminProfile();
  }, []);

 const fetchAdminProfile = async () => {
  try {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setProfileLoading(false);
      return;
    }

    const response = await fetch(`${API_URL}/auth/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    // Check if response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    console.log('🔍 Full API Response:', data);
    console.log('🔍 Response keys:', Object.keys(data));

    // Try different possible response structures
    let user = null;
    
    if (data.data && data.data.user) {
      // Structure: { data: { user: { ... } } }
      user = data.data.user;
    } else if (data.data) {
      // Structure: { data: { ... } }
      user = data.data;
    } else if (data.user) {
      // Structure: { user: { ... } }
      user = data.user;
    } else {
      // Structure: direct user object { ... }
      user = data;
    }

    console.log('🔍 Extracted user:', user);

    if (user) {
      setProfile(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    } else {
      toast.error('No user data found in response');
    }
  } catch (error) {
    console.error('Error fetching profile:', error);
    toast.error('Error loading profile');
  } finally {
    setProfileLoading(false);
  }
};
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${API_URL}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone
      })
    });

    const data = await response.json();

    console.log('Update Profile Response:', data);

    if (data) {
      // Handle different response structures for update
      const updatedUser = data.data || data.user || data;
      
      // Update localStorage with new data
      const currentUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const newUserData = {
        ...currentUser,
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        phone: updatedUser.phone
      };
      
      localStorage.setItem('adminUser', JSON.stringify(newUserData));
      toast.success('Profile updated successfully!');
    } else {
      toast.error('Failed to update profile');
    }
  } catch (error) {
    console.error('Error updating profile:', error);
    toast.error('Failed to update profile');
  } finally {
    setLoading(false);
  }
};
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (profile.newPassword !== profile.confirmPassword) {
        toast.error('New passwords do not match');
        setLoading(false);
        return;
      }

      if (profile.newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: profile.currentPassword,
          newPassword: profile.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        // Clear password fields
        setProfile(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        
        toast.success('Password changed successfully!');
      } else {
        toast.error(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getAdminUser = () => {
    try {
      return JSON.parse(localStorage.getItem('adminUser') || '{}');
    } catch (error) {
      console.error('Error parsing adminUser from localStorage:', error);
      return {};
    }
  };

  const adminUser = getAdminUser();

  if (profileLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        padding: 'var(--space-4)'
      }}>
        <div>Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <h1 className="accent-gold" style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-3xl)'),
          marginBottom: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : 'var(--space-3)')
        }}>
          Admin <span style={{ color: 'var(--text-dark)' }}>Profile</span>
        </h1>
        
        <p style={{ 
          color: 'var(--text-medium)',
          fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
        }}>
          Manage your admin account settings and preferences
        </p>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
        gap: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)')
      }}>
        
        {/* Profile Information */}
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>👤</span>
              Profile Information
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            <form onSubmit={handleProfileUpdate}>
              <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-4)') }}>
                
                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={profile.firstName || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your first name"
                    required
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={profile.lastName || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your last name"
                    required
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profile.email || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your email"
                    required
                    disabled
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      opacity: 0.7,
                      backgroundColor: 'var(--bg-light)'
                    }}
                  />
                  <div style={{ 
                    fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                    color: 'var(--text-light)', 
                    marginTop: 'var(--space-1)'
                  }}>
                    Email cannot be changed
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your phone number"
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-gold"
                  disabled={loading}
                  style={{
                    padding: isSmallScreen ? '8px 12px' : (isMobile ? '10px 16px' : '12px 24px'),
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
                  }}
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Change Password */}
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>🔒</span>
              Change Password
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            <form onSubmit={handlePasswordChange}>
              <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-4)') }}>
                
                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={profile.currentPassword || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter current password"
                    required
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={profile.newPassword || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter new password"
                    required
                    minLength="6"
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                  <div style={{ 
                    fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                    color: 'var(--text-light)', 
                    marginTop: 'var(--space-1)'
                  }}>
                    Password must be at least 6 characters
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={profile.confirmPassword || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Confirm new password"
                    required
                    style={{
                      padding: isSmallScreen ? '8px 12px' : '10px 16px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-gold"
                  disabled={loading}
                  style={{
                    padding: isSmallScreen ? '8px 12px' : (isMobile ? '10px 16px' : '12px 24px'),
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
                  }}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Account Information - Full width */}
        <div className="card card-gold" style={{ 
          gridColumn: isSmallScreen ? '1' : (isMobile ? '1' : '1 / -1')
        }}>
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>📊</span>
              Account Information
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))'),
              gap: isSmallScreen ? 'var(--space-4)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
              alignItems: isSmallScreen ? 'center' : 'flex-start'
            }}>
              
              {/* Profile Avatar and Name */}
              <div style={{ 
                textAlign: 'center',
                order: isSmallScreen ? '-1' : '0'
              }}>
                <div style={{
                  width: isSmallScreen ? '60px' : (isMobile ? '70px' : '80px'),
                  height: isSmallScreen ? '60px' : (isMobile ? '70px' : '80px'),
                  borderRadius: '50%',
                  backgroundColor: 'var(--accent-gold)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-dark)',
                  fontWeight: '600',
                  fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
                  margin: '0 auto var(--space-3)'
                }}>
                  {(profile.firstName || 'A').charAt(0).toUpperCase()}
                </div>
                <h4 style={{ 
                  marginBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)', 
                  color: 'var(--text-dark)',
                  fontSize: isSmallScreen ? 'var(--font-size-base)' : (isMobile ? 'var(--font-size-lg)' : 'var(--font-size-xl)')
                }}>
                  {profile.firstName || ''} {profile.lastName || ''}
                </h4>
                <p style={{ 
                  color: 'var(--text-light)', 
                  margin: 0,
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  {adminUser.role === 'admin' ? 'Administrator' : 'User'}
                </p>
              </div>

              {/* Account Details - Column 1 */}
              <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)' }}>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Account ID:
                  </strong>
                  <span style={{ 
                    color: 'var(--text-dark)',
                    wordBreak: 'break-word'
                  }}>
                    {adminUser.id || 'ADMIN001'}
                  </span>
                </div>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Role:
                  </strong>
                  <span style={{ 
                    color: 'var(--text-dark)',
                    backgroundColor: adminUser.role === 'admin' ? 'rgba(255, 193, 7, 0.1)' : 'var(--bg-light)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-sm)',
                    marginLeft: 'var(--space-1)'
                  }}>
                    {adminUser.role === 'admin' ? 'Administrator' : 'User'}
                  </span>
                </div>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Last Login:
                  </strong>
                  <span style={{ color: 'var(--text-dark)' }}>
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Email:
                  </strong>
                  <span style={{ 
                    color: 'var(--text-dark)',
                    wordBreak: 'break-word'
                  }}>
                    {profile.email || ''}
                  </span>
                </div>
              </div>

              {/* Account Details - Column 2 */}
              <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)' }}>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Permissions:
                  </strong>
                  <span style={{ 
                    color: 'var(--text-dark)',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-sm)',
                    marginLeft: 'var(--space-1)'
                  }}>
                    Full Access
                  </span>
                </div>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Account Status:
                  </strong>
                  <span style={{ 
                    color: 'var(--success)',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-sm)',
                    marginLeft: 'var(--space-1)'
                  }}>
                    Active
                  </span>
                </div>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Phone:
                  </strong>
                  <span style={{ 
                    color: profile.phone ? 'var(--text-dark)' : 'var(--text-light)',
                    fontStyle: profile.phone ? 'normal' : 'italic'
                  }}>
                    {profile.phone || 'Not set'}
                  </span>
                </div>
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}>
                  <strong style={{ 
                    display: 'inline-block',
                    minWidth: isSmallScreen ? '80px' : '100px',
                    color: 'var(--text-medium)'
                  }}>
                    Member Since:
                  </strong>
                  <span style={{ color: 'var(--text-dark)' }}>
                    {new Date().getFullYear()}
                  </span>
                </div>
              </div>

            </div>
            
            {/* Additional Info for Mobile */}
            {isSmallScreen && (
              <div style={{ 
                marginTop: 'var(--space-4)',
                padding: 'var(--space-3)',
                backgroundColor: 'var(--bg-light)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-light)'
              }}>
                <h5 style={{ 
                  margin: '0 0 var(--space-2) 0',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-dark)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}>
                  <span style={{ color: 'var(--accent-gold)' }}>ℹ️</span>
                  Profile Tips
                </h5>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: 'var(--space-3)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-medium)'
                }}>
                  <li>Keep your profile information up to date</li>
                  <li>Use a strong, unique password</li>
                  <li>Your email is your login ID and cannot be changed</li>
                  <li>Contact support if you need help</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;